import './JobDetailsPrint.scss';

import { IonCheckboxCustomEvent } from '@ionic/core';
import {
  CheckboxChangeEventDetail,
  IonButton,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
} from '@ionic/react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { DqText } from 'src/components/dq-text';
import { GenericMessages } from 'src/intl/GenericMessages';
import { JobMessages } from 'src/intl/JobMessages';

export interface JobDetailsPrintOptions {
  tubesCleaned: boolean;
  tubesCompletion: boolean;
  tubesTime: boolean;
  tubesDepthCleaned: boolean;
  tubesAutostrokes: boolean;
  tubesLanceSpeed: boolean;
  annotations: boolean;
  timestamps: boolean;
  pressure: boolean;
  pieCharts: boolean;
  idle: boolean;
}

interface JobDetailsPrintModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** Called when the modal is closed. */
  onCancel: () => void;
  /** Called when the modal is closed with the confirm action. */
  onConfirm: (nextValue: JobDetailsPrintOptions) => void;
}

const initValue: JobDetailsPrintOptions = {
  tubesCleaned: true,
  tubesAutostrokes: true,
  tubesCompletion: true,
  tubesDepthCleaned: true,
  tubesLanceSpeed: true,
  tubesTime: true,
  annotations: true,
  timestamps: true,
  pressure: true,
  pieCharts: true,
  idle: true,
};

export const JobDetailsPrintModal: React.FC<JobDetailsPrintModalProps> = ({ isOpen, onCancel, onConfirm }) => {
  const { formatMessage } = useIntl();

  const [checkField, setCheckField] = useState<JobDetailsPrintOptions>(initValue);

  // An effect that resets the form each time the modal is opened.
  React.useEffect(() => {
    if (isOpen) {
      setCheckField(initValue);
    }
  }, [isOpen]);

  const checkHandler = React.useCallback((e: IonCheckboxCustomEvent<CheckboxChangeEventDetail>) => {
    setCheckField(current => ({ ...current, [e.target!.name]: e.detail.checked }));
  }, []);

  const handleConfirm = React.useCallback(() => {
    onConfirm(checkField);
  }, [checkField, onConfirm]);

  return (
    <IonModal onDidDismiss={onCancel} isOpen={isOpen} className="no-print job-details-print-container">
      <IonContent>
        <div>
          <DqText variant="h1">{formatMessage(JobMessages.jobInfoPrintDialogHeader)}</DqText>
          <br />
          <DqText variant="body">{formatMessage(JobMessages.jobInfoPrintDialogSubHeader)}</DqText>
          <br />
          <br />
          <DqText variant="body-strong">{formatMessage(JobMessages.jobInfoPrintDialogOptionsHeader)}</DqText>
        </div>
        <IonList>
          <IonItem>
            <IonCheckbox
              slot="start"
              name="tubesCleaned"
              onIonChange={checkHandler}
              checked={checkField.tubesCleaned}
            />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesCleaned)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox
              slot="start"
              name="tubesCompletion"
              onIonChange={checkHandler}
              checked={checkField.tubesCompletion}
            />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesCompletion)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="tubesTime" onIonChange={checkHandler} checked={checkField.tubesTime} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesTime)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox
              slot="start"
              name="tubesDepthCleaned"
              onIonChange={checkHandler}
              checked={checkField.tubesDepthCleaned}
            />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesDepthCleaned)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox
              slot="start"
              name="tubesAutostrokes"
              onIonChange={checkHandler}
              checked={checkField.tubesAutostrokes}
            />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesAutostrokes)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox
              slot="start"
              name="tubesLanceSpeed"
              onIonChange={checkHandler}
              checked={checkField.tubesLanceSpeed}
            />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTubesLanceSpeed)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="annotations" onIonChange={checkHandler} checked={checkField.annotations} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionAnnotations)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="timestamps" onIonChange={checkHandler} checked={checkField.timestamps} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionTimestamps)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="pieCharts" onIonChange={checkHandler} checked={checkField.pieCharts} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionPieCharts)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="pressure" onIonChange={checkHandler} checked={checkField.pressure} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionPressure)}</IonLabel>
          </IonItem>
          <IonItem>
            <IonCheckbox slot="start" name="idle" onIonChange={checkHandler} checked={checkField.idle} />
            <IonLabel>{formatMessage(JobMessages.jobInfoPrintDialogOptionIdle)}</IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
      <IonFooter>
        <IonButton fill="clear" onClick={onCancel}>
          {formatMessage(GenericMessages.cancelButtonLabel)}
        </IonButton>
        <IonButton onClick={handleConfirm}>{formatMessage(GenericMessages.printButtonLabel)}</IonButton>
      </IonFooter>
    </IonModal>
  );
};

export default JobDetailsPrintModal;
